import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Hidden,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Typography,
  CircularProgress,
  Tabs,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { useLazyQuery } from '@apollo/client';
import styles from './TutorialsPage.module.scss';
import { GET_SECTIONS, GET_TUTORIALS } from '../../../api';
import TitledBackgroundHeader from '../../../components/layout/TitledBackgroundHeader';

const TutorialsPage = () => {
  const [value, setValue] = React.useState(null);
  const [getAllTutorials] = useLazyQuery(GET_TUTORIALS);
  const [getSections] = useLazyQuery(GET_SECTIONS);
  const [inputValue, setInputValue] = React.useState('');
  const [allTutorials, setAllTutorials] = React.useState([]);
  const [timeoutID, setTimeoutID] = React.useState(null);
  const [sections, setSections] = React.useState([]);
  const [loading, setLoading] = useState(false);

  const getTutorialsList = async (sectionId, searchValue = '') => {
    setLoading(true);
    const response = await getAllTutorials({
      variables: { section_id: sectionId === '0' ? null : sectionId, searchValue },
      fetchPolicy: 'network-only',
    });
    if (response.data && response.data.showAllTutorial) {
      setAllTutorials(response.data.showAllTutorial);
    }
    setLoading(false);
  };

  const getSectionsList = async () => {
    const sectionsRes = await getSections({
      variables: {
        type: 'tutorial',
      },
      fetchPolicy: 'network-only',
    });
    if (sectionsRes && sectionsRes.data && sectionsRes.data.showAllSections) {
      setSections(sectionsRes.data.showAllSections);
    }
  };

  useEffect(() => {
    (async () => {
      await getTutorialsList(value);
    })();
  }, [value]);

  useEffect(() => {
    (async () => {
      await getSectionsList(value);
    })();
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const search = (inputtedValue) => {
    setInputValue(inputtedValue);
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    const timeout = setTimeout(() => {
      getTutorialsList(value, inputtedValue);
    }, 1000);
    setTimeoutID(timeout);
  };

  return (
    <div className={styles.pageContainer}>
      <TitledBackgroundHeader isHeaderNav title="Tutorials" />
      <div className={styles.content}>
        <Box className={styles.filterBar}>
          <Hidden mdDown>
            <Tabs
              variant="scrollable"
              className={styles.filterTabs}
              value={value}
              onChange={handleChange}
              TabIndicatorProps={{
                style: {
                  display: 'none',
                },
              }}
            >
              <Tab
                sx={{
                  '&.Mui-selected': {
                    color: '#1F4A9D !important',
                    fontWeight: '600 !important',
                  },
                }}
                className={styles.tabItem}
                label="All"
                value={null}
              />
              {sections.map((section) => (
                <Tab
                  key={section.id}
                  sx={{
                    '&.Mui-selected': {
                      color: '#1F4A9D !important',
                      fontWeight: '600 !important',
                    },
                  }}
                  value={section.id}
                  className={styles.tabItem}
                  label={section.name}
                />
              ))}
            </Tabs>
          </Hidden>
          <Hidden mdUp>
            <Select
              value={`${value}`}
              onChange={(event) => setValue(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  color: '#1F4A9D',
                  fontWeight: 700,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              size="medium"
              MenuProps={{
                PaperProps: {
                  style: {
                    borderRadius: '12px',
                    padding: '12px 20px',
                    border: '1px solid #EFEFEF',
                    borderTop: '0',
                  },
                },
                sx: {
                  '&& .Mui-selected': {
                    color: '#143471 !important',
                    backgroundColor: 'transparent !important',
                  },
                },
              }}
              className={styles.filterSelect}
              renderValue={(selected) => {
                const selectedItem = sections.find((item) => item.id === selected);
                return selectedItem ? selectedItem.name : 'All';
              }}
            >
              <MenuItem value="0" className={styles.menuItem}>
                All
              </MenuItem>
              {sections.map((section) => (
                <MenuItem value={section.id} className={styles.menuItem} key={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </Hidden>
          <div className={styles.searchFilter}>
            <SearchIcon sx={{ color: '#555555' }} className={styles.searchFilterIcon} />
            <OutlinedInput
              value={inputValue}
              className={styles.searchFilterInput}
              placeholder="Search"
              onChange={(e) => search(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />
          </div>
        </Box>
        {allTutorials.length > 0 && !loading ? (
          <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12, lg: 15 }}>
            {allTutorials.map((tutorial) => (
              <Grid item xs={12} sm={4} md={3} lg={3} key={tutorial.id}>
                <Card className={styles.card}>
                  {tutorial.type !== 'pdf' ? (
                    <Link to={`/tutorial/${tutorial.id}`} component="div">
                      <CardMedia
                        className={styles.cardImage}
                        component="img"
                        image={tutorial.preview}
                      />
                      <CardContent className={styles.cardContent}>
                        <div
                          className={styles.cardTitle}
                          style={
                            tutorial.name.length > 60
                              ? {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : {}
                          }
                        >
                          {tutorial.name}
                        </div>
                      </CardContent>
                    </Link>
                  ) : (
                    <a
                      href={tutorial.file !== 'no file' ? tutorial.file : tutorial.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        className={styles.cardImage}
                        component="img"
                        image={tutorial.preview}
                      />
                      <CardContent className={styles.cardContent}>
                        <div
                          className={styles.cardTitle}
                          style={
                            tutorial.name.length > 60
                              ? {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : {}
                          }
                        >
                          {tutorial.name}
                        </div>
                      </CardContent>
                    </a>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box className={styles.noData}>
            {loading ? (
              <CircularProgress disableShrink />
            ) : (
              <Typography className={styles.noDataText}>
                More tutorials will be coming here soon
              </Typography>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default TutorialsPage;
